import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { FileSpreadsheet, Upload, AlertCircle, CheckCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export function BulkImportDialog({ open, onOpenChange, onSuccess }) {
  const [step, setStep] = useState(1);
  const [containerRef, setContainerRef] = useState("");
  const [supplier, setSupplier] = useState("");
  const [csvData, setCsvData] = useState("");
  const [parsedScooters, setParsedScooters] = useState([]);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const sampleCSV = `brand,model,year,engine_cc,color,vin,mileage,purchase_price,scooter_code,repair_priority,reception_condition
Peugeot,Kisbee,2020,50,Noir,VIN123456789,5000,400,A01,2,3
Piaggio,Liberty,2019,125,Blanc,VIN987654321,8000,600,A02,1,4
Honda,PCX,2021,125,Gris,VIN456789123,3000,800,A03,3,5`;

  const parseCSV = (csv) => {
    const lines = csv.trim().split("\n");
    if (lines.length < 2) {
      toast.error("Le fichier CSV doit contenir au moins une ligne de donnees");
      return [];
    }

    const headers = lines[0].split(",").map(h => h.trim().toLowerCase());
    const requiredHeaders = ["brand", "model"];
    
    for (const req of requiredHeaders) {
      if (!headers.includes(req)) {
        toast.error(`Colonne requise manquante: ${req}`);
        return [];
      }
    }

    const scooters = [];
    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(",").map(v => v.trim());
      if (values.length < 2 || !values[0]) continue;

      const scooter = {};
      headers.forEach((header, idx) => {
        let value = values[idx] || "";
        
        // Convert numeric fields
        if (["year", "engine_cc", "mileage", "repair_priority", "reception_condition"].includes(header)) {
          value = parseInt(value) || 0;
        } else if (["purchase_price"].includes(header)) {
          value = parseFloat(value) || 0;
        }
        
        scooter[header] = value;
      });

      // Set defaults
      scooter.year = scooter.year || new Date().getFullYear();
      scooter.repair_priority = scooter.repair_priority || 2;
      scooter.reception_condition = scooter.reception_condition || 3;

      scooters.push(scooter);
    }

    return scooters;
  };

  const handleParse = () => {
    const scooters = parseCSV(csvData);
    if (scooters.length > 0) {
      setParsedScooters(scooters);
      setStep(2);
      toast.success(`${scooters.length} scooters detectes`);
    }
  };

  const handleImport = async () => {
    setImporting(true);
    try {
      const response = await api.post("/reception/bulk-import", {
        scooters: parsedScooters,
        container_reference: containerRef,
        supplier: supplier
      });

      setImportResult(response.data);
      setStep(3);
      
      if (response.data.total_imported > 0) {
        toast.success(`${response.data.total_imported} scooters importes avec succes`);
      }
      if (response.data.total_errors > 0) {
        toast.warning(`${response.data.total_errors} erreurs lors de l'import`);
      }
    } catch (error) {
      toast.error("Erreur lors de l'import");
      console.error(error);
    } finally {
      setImporting(false);
    }
  };

  const handleClose = () => {
    setStep(1);
    setCsvData("");
    setParsedScooters([]);
    setImportResult(null);
    setContainerRef("");
    setSupplier("");
    onOpenChange(false);
    if (importResult?.total_imported > 0) {
      onSuccess();
    }
  };

  const getPriorityLabel = (p) => {
    switch (p) {
      case 1: return "Urgent";
      case 2: return "Normal";
      case 3: return "Peut attendre";
      default: return "-";
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-primary" />
            Import en Masse - Etape {step}/3
          </DialogTitle>
          <DialogDescription>
            {step === 1 && "Collez vos donnees CSV ou remplissez manuellement"}
            {step === 2 && "Verifiez les donnees avant import"}
            {step === 3 && "Resultat de l'import"}
          </DialogDescription>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="container">Reference Conteneur</Label>
                <Input
                  id="container"
                  placeholder="Ex: CONT-2024-001"
                  value={containerRef}
                  onChange={(e) => setContainerRef(e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="supplier">Fournisseur</Label>
                <Input
                  id="supplier"
                  placeholder="Ex: Importateur XYZ"
                  value={supplier}
                  onChange={(e) => setSupplier(e.target.value)}
                />
              </div>
            </div>

            <div>
              <Label htmlFor="csv">Donnees CSV</Label>
              <Textarea
                id="csv"
                placeholder="Collez votre CSV ici..."
                value={csvData}
                onChange={(e) => setCsvData(e.target.value)}
                className="font-mono text-sm h-48"
              />
            </div>

            <div className="bg-secondary/50 p-4 rounded-lg">
              <p className="text-sm font-medium mb-2">Format attendu (exemple):</p>
              <pre className="text-xs overflow-x-auto bg-black/50 p-2 rounded">
                {sampleCSV}
              </pre>
              <Button
                variant="ghost"
                size="sm"
                className="mt-2"
                onClick={() => setCsvData(sampleCSV)}
              >
                Utiliser l'exemple
              </Button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <div className="flex items-center gap-4 text-sm">
              <Badge variant="outline">{parsedScooters.length} scooters</Badge>
              {containerRef && <Badge variant="outline">Conteneur: {containerRef}</Badge>}
              {supplier && <Badge variant="outline">Fournisseur: {supplier}</Badge>}
            </div>

            <div className="border rounded-lg overflow-hidden max-h-96 overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>#</TableHead>
                    <TableHead>Code</TableHead>
                    <TableHead>Marque/Modele</TableHead>
                    <TableHead>Annee</TableHead>
                    <TableHead>VIN</TableHead>
                    <TableHead>Prix Achat</TableHead>
                    <TableHead>Priorite</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {parsedScooters.map((scooter, idx) => (
                    <TableRow key={idx}>
                      <TableCell className="font-mono">{idx + 1}</TableCell>
                      <TableCell className="font-mono">{scooter.scooter_code || "-"}</TableCell>
                      <TableCell>{scooter.brand} {scooter.model}</TableCell>
                      <TableCell>{scooter.year}</TableCell>
                      <TableCell className="font-mono text-xs">{scooter.vin || "-"}</TableCell>
                      <TableCell>{scooter.purchase_price} EUR</TableCell>
                      <TableCell>
                        <Badge variant={scooter.repair_priority === 1 ? "destructive" : "outline"}>
                          {getPriorityLabel(scooter.repair_priority)}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}

        {step === 3 && importResult && (
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              {importResult.total_imported > 0 && (
                <div className="flex items-center gap-2 text-green-500">
                  <CheckCircle className="w-5 h-5" />
                  <span>{importResult.total_imported} importes avec succes</span>
                </div>
              )}
              {importResult.total_errors > 0 && (
                <div className="flex items-center gap-2 text-red-500">
                  <AlertCircle className="w-5 h-5" />
                  <span>{importResult.total_errors} erreurs</span>
                </div>
              )}
            </div>

            {importResult.imported.length > 0 && (
              <div className="border rounded-lg p-4">
                <p className="font-medium mb-2">Scooters importes:</p>
                <div className="flex flex-wrap gap-2">
                  {importResult.imported.map((s) => (
                    <Badge key={s.scooter_id} variant="outline" className="font-mono">
                      {s.scooter_id}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {importResult.errors.length > 0 && (
              <div className="border border-red-500 rounded-lg p-4">
                <p className="font-medium text-red-500 mb-2">Erreurs:</p>
                {importResult.errors.map((err, idx) => (
                  <p key={idx} className="text-sm text-red-400">
                    Ligne {err.index + 1}: {err.error}
                  </p>
                ))}
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {step === 1 && (
            <>
              <Button variant="outline" onClick={handleClose}>
                Annuler
              </Button>
              <Button onClick={handleParse} disabled={!csvData.trim()}>
                Analyser
              </Button>
            </>
          )}
          {step === 2 && (
            <>
              <Button variant="outline" onClick={() => setStep(1)}>
                Retour
              </Button>
              <Button onClick={handleImport} disabled={importing}>
                {importing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Import en cours...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Importer {parsedScooters.length} scooters
                  </>
                )}
              </Button>
            </>
          )}
          {step === 3 && (
            <Button onClick={handleClose}>
              Fermer
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
